export const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0
});

export const number = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 });

export function jakartaDateKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function jakartaDayBounds(date = new Date()) {
  const key = jakartaDateKey(date);
  const next = new Date(`${key}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return [`${key}T00:00:00+07:00`, `${next.toISOString().slice(0, 10)}T00:00:00+07:00`];
}

export function dateLabel(value, options = {}) {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T00:00:00+07:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('id-ID', {
    timeZone: 'Asia/Jakarta',
    day: '2-digit',
    month: 'short',
    year: options.short ? undefined : 'numeric',
    hour: options.time ? '2-digit' : undefined,
    minute: options.time ? '2-digit' : undefined
  }).format(date);
}

export function ageFromBirthDate(value) {
  if (!value) return '—';
  const today = new Date(`${jakartaDateKey()}T00:00:00Z`);
  const birth = new Date(`${value}T00:00:00Z`);
  let age = today.getUTCFullYear() - birth.getUTCFullYear();
  const month = today.getUTCMonth() - birth.getUTCMonth();
  if (month < 0 || (month === 0 && today.getUTCDate() < birth.getUTCDate())) age -= 1;
  return age >= 0 ? `${age} th` : '—';
}

export function friendlyError(error) {
  const message = error?.message || String(error || 'Terjadi kesalahan.');
  if (/procurement_page_data_v084|post_procurement_supplier_return_v084|receive_procurement_supplier_refund_v084/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Penyesuaian finansial retur belum aktif. Jalankan SQL 164_supplier_return_finance.sql lalu coba lagi.';
  }
  if (/163_procurement_role_separation|role keuangan|procurement_role/i.test(message)) {
    return 'Pemisahan role Procurement belum aktif. Jalankan SQL 163_procurement_role_separation.sql, lalu login ulang.';
  }
  if (/invalid login credentials/i.test(message)) return 'Email atau kata sandi tidak cocok.';
  if (/failed to fetch/i.test(message)) return 'Tidak dapat terhubung ke server. Periksa koneksi internet.';
  if (/satusehat/i.test(message) && /edge function|functionsfetcherror|failed to send a request|lookup gagal/i.test(message)) {
    return 'Edge Function SATUSEHAT belum bisa dihubungi. Pastikan function satusehat-patient-lookup sudah di-deploy dan secret SATUSEHAT_CLIENT_ID/SATUSEHAT_CLIENT_SECRET sudah diset.';
  }
  if (/gemini|gemini-tcm-assist|shizhen/i.test(message) && /edge function|functionsfetcherror|failed to send a request/i.test(message)) {
    return 'Edge Function Gemini belum bisa dihubungi. Pastikan function gemini-tcm-assist sudah di-deploy di Supabase dan secret SHIZHEN_GEMINI_API_KEY sudah diset.';
  }
  if (/edge function|functionsfetcherror|failed to send a request/i.test(message)) {
    return 'Edge Function belum bisa dihubungi. Pastikan function terkait sudah di-deploy dan secret yang dibutuhkan sudah diset.';
  }
  if (/follow_up_contacts/i.test(message) && /row-level security|permission denied|not allowed|ditolak/i.test(message)) {
    return 'Akses log Follow-Up belum aktif. Jalankan SQL policy follow_up_contacts di docs/follow-up-daily-workflow.md lalu coba lagi.';
  }
  if (/row-level security|permission denied|not allowed/i.test(message)) {
    return 'Akun tidak mempunyai izin untuk membuka data ini.';
  }
  if (/registration_reference_data|save_registration_client|register_clinic_visit/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Fungsi Pendaftaran belum tersedia. Jalankan SQL 070_registration_write.sql di Supabase lalu coba lagi.';
  }
  if (/examination_form_data|save_clinic_examination/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Fungsi Pemeriksaan belum tersedia. Jalankan SQL 080_examination_write.sql di Supabase lalu coba lagi.';
  }
  if (/action_form_data_operational|save_clinic_action_operational/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Pemisahan Tindakan dan Pembayaran belum tersedia. Jalankan SQL 130_clinical_billing_separation.sql di Supabase lalu coba lagi.';
  }
  if (/action_form_data|save_clinic_action/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Fungsi Tindakan belum tersedia. Jalankan SQL 090_clinical_actions_bhp.sql di Supabase lalu coba lagi.';
  }
  if (/payment_form_data|settle_clinic_payment/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Fungsi Pembayaran belum tersedia. Jalankan SQL 100_payment_write.sql di Supabase lalu coba lagi.';
  }
  if (/transaction_installments|create_installment_plan|pay_installment|mark_overdue_installments|v_pending_installments/i.test(message) && /not find|does not exist|schema cache|relation .* does not exist|column .*payment_mode.* does not exist/i.test(message)) {
    return 'Fitur cicilan belum aktif. Jalankan SQL 20260817100000_installment_payments_text_ids.sql di Supabase lalu tunggu schema reload.';
  }
  if (/package_reference_data|package_page_data|create_package_purchase/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Fungsi Pembelian Paket belum tersedia. Jalankan SQL 110_package_purchase.sql di Supabase lalu coba lagi.';
  }
  if (/payment_package_options|set_ready_transaction_item_package/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Pemilihan paket pada Pembayaran belum tersedia. Jalankan SQL 120_payment_package_switch.sql di Supabase lalu coba lagi.';
  }
  if (/Tindakan yang sama tidak boleh dimasukkan dua kali/i.test(message)) {
    return 'Tindakan berulang belum diaktifkan. Jalankan SQL 140_repeated_actions_package_capacity.sql di Supabase lalu coba lagi.';
  }
  if (/pg_catalog\.greatest\(numeric, integer\) does not exist/i.test(message)) {
    return 'Perhitungan kuota paket perlu diperbaiki. Jalankan SQL 141_package_capacity_greatest_hotfix.sql di Supabase lalu coba lagi.';
  }
  if (/daily_closing_data|finalize_daily_closing/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Fungsi Tutup Kas belum tersedia. Jalankan SQL 150_daily_closing_reconciliation.sql di Supabase lalu coba lagi.';
  }
  if (/procurement_page_data_v082|save_procurement_purchase_order_v082|review_procurement_purchase_order|mark_procurement_purchase_order_ordered|record_procurement_supplier_invoice|post_procurement_goods_receipt_v082|pay_procurement_supplier_invoice|procurement_purchase_order_print_data_v082/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Alur Procurement v0.8.2 belum tersedia. Jalankan SQL 161_procurement_po_print.sql lalu 162_procurement_finance_workflow.sql di Supabase.';
  }
  if (/procurement_page_data|save_procurement_supplier|save_procurement_purchase_order|post_procurement_goods_receipt|post_procurement_supplier_return/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Fungsi Procurement belum tersedia. Jalankan SQL 160_procurement_operations.sql di Supabase lalu coba lagi.';
  }
  if (/procurement_purchase_order_print_data/i.test(message) && /not find|does not exist|schema cache/i.test(message)) {
    return 'Fungsi Cetak PO belum tersedia. Jalankan SQL 161_procurement_po_print.sql di Supabase lalu coba lagi.';
  }
  return message;
}
